import { useMutation, useQuery } from '@tanstack/react-query';
import { type ReactElement, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getBuildById } from '@/features/builds/data/builds';
import { getExecutionRunById } from '@/features/executionRuns/data/executionRuns';
import { querySuiteExecutions } from '@/features/executions/data';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects';
import { deleteResultsFile, getResultsFileBySlug, getUploadById } from '@/features/uploads/data';
import { NotFoundScreen } from '@/pages/NotFoundScreen';
import { useNotifications } from '@/providers/notificationHooks';
import {
  gridPaths,
  invalidateExecutionRuns,
  invalidateGridQuery,
  invalidateResultsFiles,
  queryKeys,
  useAccessToken,
  useInvalidateQueries,
} from '@/shared/hooks';
import { buildEqFilterValue } from '@/ui/composites/grid/gridFilters';
import { DetailPageScaffold } from '@/ui/composites/scaffolds';
import { usePageTitle } from '@/ui/hooks/usePageTitle';
import { useBreadcrumbs } from '@/ui/layout/breadcrumbsContext';
import { ResultsFileDetailContent } from './ResultsFileDetailContent';
import type { AuthSession } from '@/auth/types';
import type { ApiError } from '@/shared/data/client';
import type {
  BuildDto,
  ExecutionRunDto,
  QueryResult,
  ResultsFileDto,
  SuiteExecutionDto,
  UploadDto,
} from '@flowqa/shared';

type ResultsFileDetailPageProps = { session: AuthSession | null };

export const ResultsFileDetailPage = ({ session }: ResultsFileDetailPageProps): ReactElement => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId, setSelectedProjectId } = useProjectFilter();
  const token = useAccessToken();
  const { notify } = useNotifications();
  const invalidateQueries = useInvalidateQueries();
  const { setOverrideItems, clearOverride } = useBreadcrumbs();
  const searchSlug = useMemo(() => (slug ?? '').trim(), [slug]);

  const resultsFileQuery = useQuery<ResultsFileDto, ApiError>({
    queryKey: queryKeys.resultsFileDetail(searchSlug || null, selectedOrganisationId ?? null),
    enabled: Boolean(token && searchSlug),
    queryFn: async () =>
      await getResultsFileBySlug(searchSlug, selectedOrganisationId ?? undefined),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'queued' || status === 'processing' ? 5000 : false;
    },
  });

  const { data: resultsFile, isPending, isError, error } = resultsFileQuery;
  usePageTitle(resultsFile?.fileName ?? 'Results');
  const buildId = resultsFile?.buildId ?? null;
  const buildQuery = useQuery<BuildDto | null, ApiError>({
    queryKey: queryKeys.buildDetail(buildId, selectedOrganisationId ?? null),
    enabled: Boolean(token && buildId),
    queryFn: async () =>
      buildId ? await getBuildById(buildId, selectedOrganisationId ?? undefined) : null,
  });
  const build = buildQuery.data ?? null;
  const suiteExecutionsQuery = useQuery<QueryResult<SuiteExecutionDto>, ApiError>({
    queryKey: queryKeys.dataGrid(
      'suite-executions/results-file',
      resultsFile?.id ?? null,
      resultsFile?.projectId ?? null,
      selectedOrganisationId ?? null,
    ),
    enabled: Boolean(token && resultsFile?.id),
    queryFn: async () =>
      await querySuiteExecutions(
        resultsFile?.projectId ?? null,
        selectedOrganisationId ?? undefined,
        {
          page: 1,
          pageSize: 100,
          sorts: [{ field: 'timestamp', direction: 'desc' }],
          filters: buildEqFilterValue('resultsFileId', resultsFile?.id ?? null),
        },
      ),
  });
  const suiteExecutions = suiteExecutionsQuery.data?.items ?? [];
  const uploadId = resultsFile?.uploadId ?? null;
  const uploadQuery = useQuery<UploadDto | null, ApiError>({
    queryKey: queryKeys.uploadDetail(uploadId, selectedOrganisationId ?? null),
    enabled: Boolean(token && uploadId),
    queryFn: async () =>
      uploadId ? await getUploadById(uploadId, selectedOrganisationId ?? undefined) : null,
  });
  const upload = uploadQuery.data ?? null;
  const executionRunId = upload?.latestExecutionRunId ?? upload?.executionRunIds[0] ?? null;
  const executionRunQuery = useQuery<ExecutionRunDto | null, ApiError>({
    queryKey: queryKeys.executionRunDetail(executionRunId, selectedOrganisationId ?? null),
    enabled: Boolean(token && executionRunId),
    queryFn: async () =>
      executionRunId
        ? await getExecutionRunById(executionRunId, selectedOrganisationId ?? undefined)
        : null,
  });
  const executionRun = executionRunQuery.data ?? null;
  const deleteResultsFileMutation = useMutation<{ slug: string }, ApiError, { slug: string }>({
    mutationFn: async (input) =>
      await deleteResultsFile({ slug: input.slug }, selectedOrganisationId),
    onSuccess: async () => {
      await invalidateQueries([
        invalidateResultsFiles(selectedOrganisationId ?? null, selectedProjectId ?? null),
        invalidateExecutionRuns(selectedOrganisationId ?? null, selectedProjectId ?? null),
        invalidateGridQuery(gridPaths.resultsFiles),
        invalidateGridQuery(gridPaths.executionRuns),
        invalidateGridQuery(gridPaths.suiteExecutions),
        invalidateGridQuery(gridPaths.testExecutions),
      ]);
      notify({ message: 'Results file deleted.', tone: 'green' });
      void navigate('/execution-runs#results-files', { replace: true });
    },
    onError: (deleteError) => {
      notify({
        message: deleteError.message,
        tone: 'red',
      });
    },
  });

  const handleDeleteResultsFile = async (): Promise<void> => {
    if (!resultsFile) return;
    await deleteResultsFileMutation.mutateAsync({ slug: resultsFile.slug });
  };

  const hasInvalidSlug = !searchSlug;
  const showNotFound = hasInvalidSlug || (!isPending && resultsFileQuery.isFetched && !resultsFile);

  useEffect(() => {
    if (!resultsFile) return;
    const resultsFileProjectId = resultsFile.projectId;
    if (!selectedProjectId || resultsFileProjectId !== selectedProjectId) {
      setSelectedProjectId(resultsFileProjectId);
    }
  }, [resultsFile, selectedProjectId, setSelectedProjectId]);

  useEffect(() => {
    if (!resultsFile) return;
    setOverrideItems([
      { label: 'Results Files', href: '/execution-runs#results-files' },
      { label: resultsFile.fileName },
    ]);
  }, [resultsFile?.fileName, resultsFile?.id, setOverrideItems]);

  useEffect(
    () => () => {
      clearOverride();
    },
    [clearOverride],
  );

  if (showNotFound) {
    return (
      <NotFoundScreen
        title="Results file not found"
        body="This results file does not exist or you no longer have access to it."
        backTo="/execution-runs"
        backLabel="Back to execution runs"
      />
    );
  }

  return (
    <DetailPageScaffold
      session={session}
      sessionMessage="Sign in to view results details."
      organisationId={selectedOrganisationId}
      requireProject={false}
      projectMessage="Select a project to view results details."
      projectId={selectedProjectId}
      isPending={isPending}
      isError={isError}
      error={error}
      errorMessage="Failed to load results"
      data={resultsFile}
      loadingFallback={<></>}
    >
      {(resolvedResultsFile) => {
        return (
          <ResultsFileDetailContent
            title={resolvedResultsFile.fileName}
            resultsFile={resolvedResultsFile}
            build={build}
            upload={upload}
            executionRun={executionRun}
            suiteExecutions={suiteExecutions}
            suiteExecutionsLoading={suiteExecutionsQuery.isLoading}
            onDelete={handleDeleteResultsFile}
            deleteLoading={deleteResultsFileMutation.isPending}
          />
        );
      }}
    </DetailPageScaffold>
  );
};
